import { useState } from "react";
import { useReportDataByYear } from "@/hooks/useReportData";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { parse, format } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronDown, ChevronRight } from "lucide-react";

export function ReportView() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { reportByCurrency, categoryReportByCurrency } =
    useReportDataByYear(year);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const toggleExpand = (currency: string, month: string) => {
    const key = `${currency}-${month}`;
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  console.log("categoryReportByCurrency", categoryReportByCurrency);

  return (
    <div className="px-4 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Reporte Financiero</h2>
        <Select
          defaultValue={String(currentYear)}
          onValueChange={(value) => setYear(Number(value))}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="monthly" className="w-full">
        <TabsList>
          <TabsTrigger value="monthly">Por Mes</TabsTrigger>
          <TabsTrigger value="categories">Por Categoría</TabsTrigger>
        </TabsList>

        {/* --- RESUMEN MENSUAL --- */}
        <TabsContent value="monthly">
          {Object.entries(reportByCurrency || {}).map(([currency, months]) => (
            <div key={currency} className="mb-8">
              <h3 className="text-xl font-semibold mb-2">Moneda: {currency}</h3>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Mes</TableHead>
                    <TableHead>Ingresos</TableHead>
                    <TableHead>Gastos</TableHead>
                    <TableHead>Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(months)
                    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
                    .map(([month, data]) => {
                      const key = `${currency}-${month}`;
                      return (
                        <>
                          <TableRow
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleExpand(currency, month)}
                          >
                            <TableCell className="w-4">
                              {expanded[key] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </TableCell>
                            <TableCell>
                              {format(
                                parse(month, "yyyy-MM", new Date()),
                                "MMMM",
                                { locale: es }
                              ).toUpperCase()}
                            </TableCell>
                            <TableCell>
                              {currency} {data.income.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {currency} {data.expense.toFixed(2)}
                            </TableCell>
                            <TableCell
                              className={
                                data.balance >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {currency} {data.balance.toFixed(2)}
                            </TableCell>
                          </TableRow>

                          {expanded[key] && (
                            <TableRow className="bg-muted/40">
                              <TableCell colSpan={5} className="p-3">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>#</TableHead>
                                      <TableHead>Cuenta</TableHead>
                                      <TableHead>Ingresos</TableHead>
                                      <TableHead>Gastos</TableHead>
                                      <TableHead>Balance</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {Object.entries(data.accounts).map(
                                      ([accountName, acc], i) => (
                                        <TableRow key={accountName}>
                                          <TableCell>#{i + 1}</TableCell>
                                          <TableCell>{accountName}</TableCell>
                                          <TableCell>
                                            {currency} {acc.income.toFixed(2)}
                                          </TableCell>
                                          <TableCell>
                                            {currency} {acc.expense.toFixed(2)}
                                          </TableCell>
                                          <TableCell
                                            className={
                                              acc.balance >= 0
                                                ? "text-green-600"
                                                : "text-red-600"
                                            }
                                          >
                                            {currency} {acc.balance.toFixed(2)}
                                          </TableCell>
                                        </TableRow>
                                      )
                                    )}
                                  </TableBody>
                                </Table>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          ))}
        </TabsContent>

        {/* --- CATEGORÍAS POR MONEDA --- */}
        <TabsContent value="categories">
          {Object.entries(categoryReportByCurrency || {}).map(
            ([currency, report]) => (
              <div key={currency} className="mb-6">
                <h3 className="text-xl font-semibold mb-2">
                  Moneda: {currency}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Ingresos por Categoría</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Categoría</TableHead>
                            <TableHead>Monto</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {report.income.map((item) => (
                            <TableRow key={item.category}>
                              <TableCell>{item.category}</TableCell>
                              <TableCell>
                                {currency} {item.amount.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Gastos por Categoría</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Categoría</TableHead>
                            <TableHead>Monto</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {report.expense.map((item) => (
                            <TableRow key={item.category}>
                              <TableCell>{item.category}</TableCell>
                              <TableCell>
                                {currency} {item.amount.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
